import { Buffer } from "buffer";
import { decompressSync, gunzipSync } from "fflate";
import { inflate, ungzip } from "pako";

const AIM_HCNF = [0x3c, 0x68, 0x43, 0x4e, 0x46] as const; // <hCNF
const AIM_HDRK = [0x3c, 0x68, 0x44, 0x52, 0x4b] as const; // <hDRK

export function isZlibWrapped(raw: Buffer | Uint8Array): boolean {
  return (
    raw.length >= 2 &&
    raw[0] === 0x78 &&
    (raw[1] === 0x9c || raw[1] === 0x01 || raw[1] === 0xda || raw[1] === 0x5e)
  );
}

function toUint8Array(raw: Buffer | Uint8Array): Uint8Array {
  if (raw instanceof Uint8Array) {
    return new Uint8Array(raw.buffer, raw.byteOffset, raw.byteLength);
  }

  return new Uint8Array(raw);
}

function hasAimHeader(raw: Buffer | Uint8Array): boolean {
  if (raw.length < 5) {
    return false;
  }

  const tag = [raw[0], raw[1], raw[2], raw[3], raw[4]];
  return (
    tag.every((byte, index) => byte === AIM_HCNF[index]) ||
    tag.every((byte, index) => byte === AIM_HDRK[index])
  );
}

function formatHeadHex(raw: Buffer | Uint8Array, length = 8): string {
  const end = Math.min(raw.length, length);
  const parts: string[] = [];
  for (let index = 0; index < end; index += 1) {
    parts.push(raw[index]!.toString(16).padStart(2, "0"));
  }
  return parts.join(" ");
}

export function inflateXrz(raw: Buffer | Uint8Array): Buffer {
  const input = toUint8Array(raw);

  try {
    return Buffer.from(decompressSync(input));
  } catch (fflateError) {
    try {
      return Buffer.from(gunzipSync(input));
    } catch {
      try {
        return Buffer.from(inflate(input));
      } catch {
        try {
          return Buffer.from(ungzip(input));
        } catch {
          const detail =
            fflateError instanceof Error ? fflateError.message : String(fflateError);
          throw new Error(
            `Could not decompress telemetry file (${raw.length} bytes). The download may be incomplete — delete it, reconnect to the MyChron Wi-Fi, and download again. (${detail})`,
          );
        }
      }
    }
  }
}

export function loadTelemetryBuffer(raw: Buffer | Uint8Array): Buffer {
  if (!isZlibWrapped(raw)) {
    return Buffer.isBuffer(raw) ? raw : Buffer.from(raw);
  }

  return inflateXrz(raw);
}

export function readTelemetryBuffer(raw: Buffer | Uint8Array): Buffer {
  return loadTelemetryBuffer(raw);
}

export function detectCorruptDownload(raw: Buffer | Uint8Array): string | null {
  if (raw.length >= 18 && raw[0] === 0x6e && raw[1] === 0x61 && raw[2] === 0x6d && raw[3] === 0x65) {
    // "name,size,date,..." CSV from an incomplete download
    return "File contains session-list CSV instead of telemetry binary (incomplete/wrong download).";
  }
  return null;
}

/** AIM download frames carry up to this many bytes of file payload per chunk. */
export const AIM_DOWNLOAD_CHUNK_BYTES = 65472;

export function detectDuplicateDownloadChunks(raw: Buffer | Uint8Array): string | null {
  const chunk = AIM_DOWNLOAD_CHUNK_BYTES;
  const frameCount = Math.floor(raw.length / chunk);
  if (frameCount < 4) {
    return null;
  }

  for (let index = 2; index < frameCount - 1; index += 2) {
    const left = raw.subarray(index * chunk, (index + 1) * chunk);
    const right = raw.subarray((index + 1) * chunk, (index + 2) * chunk);
    if (left.length === chunk && right.length === chunk && Buffer.from(left).equals(Buffer.from(right))) {
      const byteOffset = (index + 1) * chunk;
      return `Download contains a duplicated chunk at byte ${byteOffset} (likely a double-ACK during download). Delete this file and download again.`;
    }
  }

  return null;
}

export function validateTelemetryBuffer(raw: Buffer | Uint8Array): string | null {
  const corrupt = detectCorruptDownload(raw);
  if (corrupt) {
    return corrupt;
  }

  const duplicate = detectDuplicateDownloadChunks(raw);
  if (duplicate) {
    return duplicate;
  }

  if (!isZlibWrapped(raw)) {
    if (!hasAimHeader(raw)) {
      return "File does not look like a valid AIM telemetry download.";
    }
    return null;
  }

  try {
    const decoded = inflateXrz(raw);
    if (!hasAimHeader(decoded)) {
      return `Decompressed data is not a valid AIM telemetry file (missing <hCNF header, got ${formatHeadHex(decoded)}).`;
    }
    return null;
  } catch (error) {
    return error instanceof Error ? error.message : String(error);
  }
}
